import {ComponentClass, FunctionComponent} from 'react';
import {Texture, DisplayObject, filters, Graphics, Point, Rectangle, SCALE_MODES, Sprite} from 'pixi.js';
import {DeviceQuality, IBrowser, IService, Map, SystemComponentType} from '@gongxigames/framework/core/types';
import {CoreDI} from '@gongxigames/framework/core/configs';
import {asyncRenderComponentToContainer} from '@gongxigames/framework/core/fragments/react';
import {inject, injectable} from '@gongxigames/framework/core/installer';
import {FullScreenFader, FullScreenContainer, FullScreenContainerOptions, FullScreenFaderOptions} from '@gongxigames/framework/core/fragments/display';
import {System} from '@gongxigames/framework/core/services/system/System';
import {Device} from '@gongxigames/framework/core/services/Device';
import {ScreenManager} from '@gongxigames/framework/core/services/screenManager/ScreenManager';
import {UpdateManager} from '@gongxigames/framework/core/services/UpdateManager';

export interface ICachedDisplayObject {
    texture: Texture;
    position: Point;
}

/**
 * @deprecated
 * TODO: make it optional to build in a bundle.js
 */
@injectable()
export class DisplayObjectUtils implements IService {
    public static NAME: symbol = CoreDI.DisplayObjectUtils;
    public type: SystemComponentType = 'service';
    protected static _CACHED_BLURRED_OBJECTS_DATA: Map<ICachedDisplayObject> = {};
    protected static _CACHED_TEXTURES: Map<Texture> = {};

    public static readonly LOW_DEVICE_RESOLUTION: number = 0.8;

    @inject(CoreDI.System)
    protected _system!: System;

    @inject(CoreDI.Device)
    protected _device!: Device;

    @inject(CoreDI.ScreenManager)
    protected _screenManager!: ScreenManager;

    @inject(CoreDI.UpdateManager)
    protected _updateManager!: UpdateManager;

    @inject(CoreDI.Browser)
    protected _browser!: IBrowser;

    public static clearCache(): void {
        DisplayObjectUtils._CACHED_BLURRED_OBJECTS_DATA = {};
        DisplayObjectUtils._CACHED_TEXTURES = {};
    }

    public static getBlurredSpriteFromCache(name: number | string): Sprite | null {
        const cachedSymbol = DisplayObjectUtils._CACHED_BLURRED_OBJECTS_DATA[name];

        if (cachedSymbol) {
            const {texture, position} = cachedSymbol;
            const sprite = new Sprite(texture);
            const {x, y} = position;

            sprite.position.set(x, y);

            return sprite;
        }

        return null;
    }

    public static createSpriteFromTextureCache(textureName: number | string): Sprite {
        const texture = DisplayObjectUtils._CACHED_TEXTURES[textureName];

        if (texture) {
            return new Sprite(texture);
        }

        throw new Error(`cant find texture ${name}`);
    }

    public destroy(): void {
        DisplayObjectUtils.clearCache();
    }

    /**
     * Draw texture with usages source DisplayObject
     * @param container
     * @param region
     */
    public generateTexture(container: DisplayObject, region?: Rectangle): Texture {
        let resolution = this._screenManager.defaultViewportResolution;

        if (this._device.quality <= DeviceQuality.LOW) {
            resolution *= DisplayObjectUtils.LOW_DEVICE_RESOLUTION;
        }

        return this._system.renderer.generateTexture(container, {
            scaleMode: SCALE_MODES.NEAREST,
            resolution,
            region
        });
    }

    public async asyncGenerateTexture(container: DisplayObject, region?: Rectangle): Promise<Texture> {
        return new Promise(res => {
            this._updateManager.addTimer(() => {
                const texture = this.generateTexture(container, region);

                res(texture);
            }, 0, true);
        });
    }

    public async asyncGenerateTextureFromReactComponent<P extends {}>(
        component: FunctionComponent<P> | ComponentClass<P> | string,
        props?: P | null,
        region?: Rectangle,
        callback?: (texture: Texture) => void
    ): Promise<Texture> {
        const container = await asyncRenderComponentToContainer(this._browser, component, props);
        const texture = await this.asyncGenerateTexture(container, region);

        if (callback) {
            callback(texture);
        }

        return texture;
    }

    public textureFromCacheToBase64(name: string): string {
        const sprite = DisplayObjectUtils.createSpriteFromTextureCache(name);

        return this.spriteToBase64(sprite);
    }

    public textureToBase64(srcTexture: Texture): string {
        const sprite = new Sprite(srcTexture);

        return this.spriteToBase64(sprite);
    }

    public spriteToBase64(sprite: Sprite): string {
        const resolution = this._screenManager.defaultViewportResolution;
        const texture = this._system.renderer.generateTexture(sprite, {
            scaleMode:  SCALE_MODES.LINEAR,
            resolution,
        });

        return this._system.renderer.plugins.extract.base64(texture);
    }

    public addCachedDisplayObjects(cachedSymbols: Map<ICachedDisplayObject>): void {
        for (const name in cachedSymbols) {
            if (cachedSymbols.hasOwnProperty(name)) {
                DisplayObjectUtils._CACHED_BLURRED_OBJECTS_DATA[name] = cachedSymbols[name];
            }
        }
    }

    public addTexturesToCache(textures: Map<Texture>): void {
        for (const name in textures) {
            if (textures.hasOwnProperty(name)) {
                DisplayObjectUtils._CACHED_TEXTURES[name] = textures[name];
            }
        }
    }

    public addBlurFilter(displayObject: DisplayObject, blurY = 0, blurX = 0): DisplayObject {
        if (!blurX && !blurY) {
            return displayObject;
        }

        const blurFilter = new filters.BlurFilter();

        blurFilter.blurY = blurY;
        blurFilter.blurX = blurX;

        displayObject.filters = [blurFilter];

        return displayObject;
    }

    /**
     * Create Graphics with params.
     * @param alpha     apply to fader, NOT used in beginFill function
     * @param width
     * @param height
     * @param color     default value is black
     */
    public createFader(alpha: number, width: number, height: number, color = 0x000000): Graphics {
        const fader = new Graphics();

        fader.beginFill(color);
        fader.drawRect(0, 0, 1, 1);
        fader.endFill();
        fader.width = width;
        fader.height = height;
        fader.alpha = alpha;

        return fader;
    }

    public createFullScreenFader(options: FullScreenFaderOptions = {}): FullScreenFader {
        options.screenParameters ??= this._screenManager.getScreenState();

        return new FullScreenFader(options);
    }

    public createFullScreenContainer(options: FullScreenContainerOptions): FullScreenContainer {
        options.screenParameters ??= this._screenManager.getScreenState();

        return new FullScreenContainer(options);
    }

    /**
     * Copies the texture from the source to the target. For Sprite and Mesh types
     * @param target
     * @param source
     */
    public copyTexture(target: Sprite, source: Sprite): void {
        target.texture = source.texture;
    }
}
